import { AlertTriangle, Mail, Trash2, UserPlus } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Platform, StyleSheet, useWindowDimensions } from 'react-native';
import { Badge } from '../../../components/primitives/Badge';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { Divider } from '../../../components/primitives/Divider';
import { Input } from '../../../components/primitives/Input';
import { Select } from '../../../components/primitives/Select';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { type TranslationParams, useTranslation } from '../../../contexts/TranslationContext';
import { useTeamRealtime } from '../../../hooks/useTeamRealtime';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import {
  useCreateInvite,
  useInvites,
  useMembers,
  useRemoveMember,
  useRevokeInvite,
  useSubscription,
  useUpdateMemberRole,
} from '../hooks/useSettings';
import type { InviteData, MemberData } from '../types';
import { SettingsSection } from './SettingsSection';

const ROLE_KEYS: { value: string; labelKey: string }[] = [
  { value: 'member', labelKey: 'common.roles.member' },
  { value: 'admin', labelKey: 'common.roles.admin' },
];

const ROLE_KEYS_WITH_OWNER = [
  ...ROLE_KEYS,
  { value: 'viewer', labelKey: 'common.roles.viewer' },
  { value: 'owner', labelKey: 'common.roles.owner' },
];

const ROLE_BADGE_COLOR: Record<string, 'cyan' | 'purple' | 'amber' | 'neutral'> = {
  owner: 'purple',
  admin: 'amber',
  member: 'cyan',
  viewer: 'neutral',
};

function formatRoleLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function getInitials(fullName: string): string {
  if (!fullName) return '?';
  return fullName.charAt(0).toUpperCase();
}

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

function confirmRemoveMember(
  memberName: string,
  onConfirm: () => void,
  t: (key: string, params?: TranslationParams) => string,
) {
  const message = t('settings.team.removeMember.message', { name: memberName });
  if (Platform.OS === 'web') {
    const confirmed = globalThis.confirm(message);
    if (confirmed) onConfirm();
  } else {
    Alert.alert(t('settings.team.removeMember.title'), message, [
      { text: t('common.actions.cancel'), style: 'cancel' },
      { text: t('common.actions.remove'), style: 'destructive', onPress: onConfirm },
    ]);
  }
}

function confirmRevokeInvite(
  email: string,
  onConfirm: () => void,
  t: (key: string, params?: TranslationParams) => string,
) {
  const message = t('settings.team.revokeInvite.message', { email });
  if (Platform.OS === 'web') {
    const confirmed = globalThis.confirm(message);
    if (confirmed) onConfirm();
  } else {
    Alert.alert(t('settings.team.revokeInvite.title'), message, [
      { text: t('common.actions.cancel'), style: 'cancel' },
      { text: t('common.actions.revoke'), style: 'destructive', onPress: onConfirm },
    ]);
  }
}

function MemberRow({
  member,
  isOwner,
  isSelf,
  orgId,
  updateRole,
  removeMember,
  roleOptions,
  t,
  rowIndex,
  totalRows,
}: Readonly<{
  member: MemberData;
  isOwner: boolean;
  isSelf: boolean;
  orgId: string;
  updateRole: ReturnType<typeof useUpdateMemberRole>;
  removeMember: ReturnType<typeof useRemoveMember>;
  roleOptions: { value: string; label: string }[];
  t: (key: string, params?: TranslationParams) => string;
  rowIndex: number;
  totalRows: number;
}>) {
  const badgeColor = ROLE_BADGE_COLOR[member.role] ?? 'neutral';
  const showControls = isOwner && !isSelf;
  const { width } = useWindowDimensions();
  const stackControls = showControls && width < 768;

  const avatar = (
    <Box
      width={40}
      height={40}
      borderRadius="full"
      backgroundColor={colors.primary.muted}
      alignItems="center"
      justifyContent="center"
    >
      <Text variant="body" weight="semibold" color={colors.primary[500]}>
        {getInitials(member.fullName)}
      </Text>
    </Box>
  );

  const info = (
    <VStack spacing="xxs" style={styles.memberInfo}>
      <Text
        variant="body"
        weight="medium"
        color={colors.text.primary}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {member.fullName}
      </Text>
      <Text variant="caption" color={colors.text.secondary} numberOfLines={1} ellipsizeMode="tail">
        {member.email}
      </Text>
    </VStack>
  );

  const controls = (
    <HStack
      spacing="sm"
      align="center"
      style={{
        flexShrink: 0,
        position: 'relative',
        zIndex: tokens.zIndex.dropdown + (totalRows - rowIndex),
      }}
    >
      <Select
        variant="dropdown"
        options={roleOptions}
        value={member.role}
        onChange={(newRole) =>
          updateRole.mutate({
            orgId,
            userId: member.userId,
            role: newRole,
          })
        }
        style={stackControls ? styles.roleSelectStacked : styles.roleSelect}
      />
      <Box
        pressable
        onPress={() =>
          confirmRemoveMember(
            member.fullName,
            () => removeMember.mutate({ orgId, userId: member.userId }),
            t,
          )
        }
        padding="sm"
        borderRadius="md"
        backgroundColor={colors.status.error.bg}
        accessibilityLabel={`Remove ${member.fullName}`}
      >
        <Trash2 size={16} color={colors.status.error.main} />
      </Box>
    </HStack>
  );

  if (stackControls) {
    return (
      <VStack spacing="sm" style={styles.memberRow}>
        <HStack spacing="md" align="center">
          {avatar}
          {info}
        </HStack>
        {controls}
      </VStack>
    );
  }

  return (
    <HStack spacing="md" align="center" style={styles.memberRow}>
      {avatar}
      {info}
      {showControls ? (
        controls
      ) : (
        <Badge color={badgeColor} size="sm" style={styles.memberTrailing}>
          {formatRoleLabel(member.role)}
        </Badge>
      )}
    </HStack>
  );
}

function InviteRow({
  invite,
  orgId,
  revokeInvite,
  t,
}: Readonly<{
  invite: InviteData;
  orgId: string;
  revokeInvite: ReturnType<typeof useRevokeInvite>;
  t: (key: string, params?: TranslationParams) => string;
}>) {
  const badgeColor = ROLE_BADGE_COLOR[invite.role] ?? 'neutral';

  return (
    <HStack spacing="md" align="center" style={styles.memberRow}>
      <Box
        width={40}
        height={40}
        borderRadius="full"
        backgroundColor={colors.status.warning.bg}
        alignItems="center"
        justifyContent="center"
      >
        <Mail size={18} color={colors.status.warning.main} />
      </Box>

      <VStack spacing="xxs" style={styles.memberInfo}>
        <Text
          variant="body"
          weight="medium"
          color={colors.text.primary}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {invite.email}
        </Text>
        <Text
          variant="caption"
          color={colors.text.secondary}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {t('settings.team.invited')} {formatDate(invite.createdAt)}
        </Text>
      </VStack>

      <HStack spacing="sm" align="center" style={styles.memberTrailing}>
        <Badge color={badgeColor} size="sm">
          {formatRoleLabel(invite.role)}
        </Badge>
        <Box
          pressable
          onPress={() =>
            confirmRevokeInvite(
              invite.email,
              () => revokeInvite.mutate({ orgId, inviteId: invite.id }),
              t,
            )
          }
          padding="sm"
          borderRadius="md"
          backgroundColor={colors.status.error.bg}
          accessibilityLabel={`Revoke invite for ${invite.email}`}
        >
          <Trash2 size={16} color={colors.status.error.main} />
        </Box>
      </HStack>
    </HStack>
  );
}

export function TeamSection() {
  const { backendUser, currentOrganization, getAccessToken } = useAuth();
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const mp = useMixpanel();
  const memberships: Array<{
    organization?: { id: string };
    organizationId?: string;
    id?: string;
    role: string;
  }> = (backendUser as any)?.organizationMembers ?? (backendUser as any)?.organizations ?? [];
  const orgMembership = memberships.find(
    (o) => (o.organization?.id ?? o.organizationId ?? o.id) === currentOrganization?.id,
  );
  const currentRole = orgMembership?.role ?? 'sales_rep';
  const isOwner = currentRole === 'owner';
  const isAdmin = currentRole === 'admin';
  const canManage = isOwner || isAdmin;

  const orgId = currentOrganization?.id;

  useTeamRealtime({ organizationId: orgId, enabled: !!orgId, getAccessToken });

  const { data: membersResponse, isLoading: membersLoading } = useMembers(orgId);
  const { data: invites, isLoading: invitesLoading } = useInvites(orgId);
  const { data: subscription } = useSubscription(canManage ? orgId : undefined);

  const createInvite = useCreateInvite();
  const revokeInvite = useRevokeInvite();
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  const members = membersResponse?.data ?? [];
  const isLoading = membersLoading || invitesLoading;

  const maxSeats = subscription?.limits.maxSeats ?? -1;
  const currentSeats = subscription?.usage.currentSeats ?? 0;
  const seatsUnlimited = maxSeats === -1;
  const atSeatLimit = !seatsUnlimited && currentSeats >= maxSeats;

  const subscriptionRef = useRef(subscription);
  subscriptionRef.current = subscription;
  const mpRef = useRef(mp);
  mpRef.current = mp;

  useEffect(() => {
    if (atSeatLimit && subscriptionRef.current) {
      trackEvent(mpRef.current, 'org.seat_limit_upsell_shown', {
        plan: subscriptionRef.current.plan,
        current_seats: subscriptionRef.current.usage.currentSeats,
        max_seats: subscriptionRef.current.limits.maxSeats,
      });
    }
  }, [atSeatLimit]);

  const handleSendInvite = () => {
    if (!inviteEmail.trim() || !orgId || atSeatLimit) return;
    createInvite.mutate(
      { orgId, email: inviteEmail.trim(), role: inviteRole },
      {
        onSuccess: () => {
          setInviteEmail('');
          setInviteRole('member');
          showSuccess(t('settings.team.inviteSent'));
        },
        onError: (error) => {
          const errorCode = (error as { response?: { data?: { errorCode?: string } } })?.response
            ?.data?.errorCode;
          if (errorCode === 'SEAT_LIMIT_REACHED') {
            // Only the seat-limit case needs a local side-effect (subscription invalidation).
            // All other errors are surfaced by the global MutationCache.onError handler.
            showError(t('settings.team.seatLimitMessage'));
          }
        },
      },
    );
  };

  const translateRoleKeys = (keys: { value: string; labelKey: string }[]) =>
    keys.map((k) => ({ value: k.value, label: t(k.labelKey) }));
  const roleOptions = isOwner
    ? translateRoleKeys(ROLE_KEYS_WITH_OWNER)
    : translateRoleKeys(ROLE_KEYS);

  if (isLoading) {
    return (
      <SettingsSection title={t('settings.team.title')}>
        <Box alignItems="center" justifyContent="center" minHeight={120}>
          <ActivityIndicator size="large" color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.text.secondary} style={styles.loadingText}>
            {t('settings.team.loading')}
          </Text>
        </Box>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection title={t('settings.team.title')}>
      <VStack spacing="sm">
        {members.map((member, rowIndex) => (
          <MemberRow
            key={member.userId}
            member={member}
            isOwner={isOwner}
            isSelf={member.userId === backendUser?.id}
            orgId={orgId!}
            updateRole={updateRole}
            removeMember={removeMember}
            roleOptions={roleOptions}
            t={t}
            rowIndex={rowIndex}
            totalRows={members.length}
          />
        ))}
        {members.length === 0 && (
          <Text variant="body" color={colors.text.secondary}>
            {t('settings.team.noMembers')}
          </Text>
        )}
      </VStack>

      {canManage && (
        <>
          <Divider color={colors.border.subtle} marginY="sm" />

          <Text variant="body" weight="medium" color={colors.text.primary}>
            {t('settings.team.pendingInvites')}
          </Text>

          <VStack spacing="sm">
            {(invites ?? []).length > 0 ? (
              (invites ?? []).map((invite) => (
                <InviteRow
                  key={invite.id}
                  invite={invite}
                  orgId={orgId!}
                  revokeInvite={revokeInvite}
                  t={t}
                />
              ))
            ) : (
              <Text variant="body" color={colors.text.secondary}>
                {t('settings.team.noPending')}
              </Text>
            )}
          </VStack>

          <Divider color={colors.border.subtle} marginY="sm" />

          <HStack spacing="sm" align="center" justify="between">
            <Text variant="body" weight="medium" color={colors.text.primary}>
              {t('settings.team.inviteMember')}
            </Text>
            {subscription && !seatsUnlimited && (
              <Text variant="caption" color={colors.text.secondary}>
                {t('settings.team.seatUsage', { current: currentSeats, max: maxSeats })}
              </Text>
            )}
          </HStack>

          {atSeatLimit && (
            <Box
              backgroundColor={colors.status.warning.bg}
              borderRadius="md"
              padding="md"
              accessibilityRole="alert"
            >
              <HStack spacing="sm" align="center">
                <AlertTriangle size={18} color={colors.status.warning.main} />
                <VStack spacing="xxs" style={styles.memberInfo}>
                  <Text variant="bodySmall" weight="medium" color={colors.text.primary}>
                    {t('settings.team.seatLimitTitle')}
                  </Text>
                  <Text variant="caption" color={colors.text.secondary}>
                    {t('settings.team.seatLimitMessage')}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          )}

          <VStack spacing="md">
            <Input
              label={t('settings.profile.email')}
              placeholder={t('settings.team.emailPlaceholder')}
              value={inviteEmail}
              onChangeText={setInviteEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
            />

            <VStack spacing="xs" style={{ position: 'relative', zIndex: tokens.zIndex.dropdown }}>
              <Text variant="bodySmall" color={colors.text.primary} style={styles.selectLabel}>
                {t('settings.team.role')}
              </Text>
              <Select
                variant="dropdown"
                options={roleOptions}
                value={inviteRole}
                onChange={setInviteRole}
              />
            </VStack>

            <Button
              variant="primary"
              size="md"
              onPress={handleSendInvite}
              disabled={!inviteEmail.trim() || createInvite.isPending || atSeatLimit}
              loading={createInvite.isPending}
            >
              <HStack spacing="sm" align="center">
                <UserPlus size={18} color={colors.background.primary} />
                <Text variant="body" weight="medium" color={colors.background.primary}>
                  {t('common.actions.sendInvite')}
                </Text>
              </HStack>
            </Button>
          </VStack>
        </>
      )}
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  memberRow: {
    width: '100%',
    paddingVertical: tokens.spacing.sm,
  },
  memberInfo: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  memberTrailing: {
    flexShrink: 0,
  },
  roleSelect: {
    width: 140,
  },
  roleSelectStacked: {
    flex: 1,
  },
  loadingText: {
    marginTop: tokens.spacing.md,
  },
  selectLabel: {
    fontWeight: '500',
  },
});
